import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SortIcon } from '@/components/shared/reports/SortIcon';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PostingLogRow, SortKey, SortDir } from '@/utils/PostingLog/types';
import { formatDate, formatCurrency } from '@/utils/PostingLog/formatters';
import { getLineDisplayStatus } from '@/utils/PostingLog/mappers';
import { StatusBadge } from './StatusBadge';

interface LineTableProps {
  lines: PostingLogRow[];
  retryingIds: Set<string>;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort?: (key: SortKey) => void;
  variant?: 'default' | 'drilldown';
  compact?: boolean;
  columnVisibility?: Record<string, boolean>;
}

export const LineTable: React.FC<LineTableProps> = ({
  lines, retryingIds, sortKey, sortDir, onSort, variant = 'default', compact = false, columnVisibility,
}) => {
  const col = (id: string) => !(variant === 'drilldown' && (id === 'employeeName' || id === 'externalTransactionId'))
    && columnVisibility?.[id] !== false;
  const SortHeader: React.FC<{ col: SortKey; label: string; className?: string }> = ({ col: sortCol, label, className }) => (
    <TableHead
      className={cn('whitespace-nowrap', onSort && 'cursor-pointer select-none', className)}
      onClick={() => onSort?.(sortCol)}
    >
      {label}
      {onSort && <SortIcon col={sortCol} current={sortKey} dir={sortDir} />}
    </TableHead>
  );

  return (
    <div className="rounded-md border overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {col('employeeName') && <SortHeader col="employeeName" label="Employee" />}
              {col('transactionDate') && <SortHeader col="transactionDate" label="Date" />}
              {col('projectName') && <SortHeader col="projectName" label="Project" />}
              {col('costCentreName') && <TableHead>Cost Centre</TableHead>}
              {col('quantity') && <SortHeader col="quantity" label="Hours" className="text-right" />}
              {col('price') && <TableHead className="text-right">Rate</TableHead>}
              {col('upliftValue') && <TableHead className="text-right">Uplift</TableHead>}
              {col('status') && <TableHead>Status</TableHead>}
              {col('externalTransactionId') && <TableHead className="whitespace-nowrap">ERP Ref</TableHead>}
              {col('errorMessage') && <TableHead className={compact ? 'max-w-[140px]' : 'max-w-[200px]'}>Error</TableHead>}
            </TableRow>
          </TableHeader>

          <TableBody>
            {lines.map((row) => {
              const displayStatus = getLineDisplayStatus(row);
              const isFailed   = displayStatus === 'Failed';
              const isRetrying = retryingIds.has(row.id);

              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    'transition-colors',
                    isFailed && 'bg-error-50/30 shadow-[inset_2px_0_0_0_var(--color-error-500)] dark:bg-error-950/10',
                  )}
                >
                  {col('employeeName') && <TableCell className="text-sm font-medium whitespace-nowrap">{row.employeeName}</TableCell>}
                  {col('transactionDate') && <TableCell className="text-sm whitespace-nowrap">{formatDate(row.transactionDate)}</TableCell>}
                  {col('projectName') && <TableCell className="text-sm">{row.projectName}</TableCell>}
                  {col('costCentreName') && <TableCell className="text-sm text-muted-foreground">{row.costCentreName}</TableCell>}
                  {col('quantity') && <TableCell className="text-sm text-right">{row.quantity.toFixed(2)}</TableCell>}
                  {col('price') && <TableCell className="text-sm text-right whitespace-nowrap">{formatCurrency(row.price)}</TableCell>}
                  {col('upliftValue') && (
                    <TableCell className="text-sm text-right whitespace-nowrap">
                      {row.upliftValue !== 0
                        ? formatCurrency(row.upliftValue)
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}

                  {/* Status */}
                  {col('status') && (
                    <TableCell>
                      {isRetrying
                        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        : <StatusBadge status={displayStatus} />}
                    </TableCell>
                  )}

                  {/* ERP Ref */}
                  {col('externalTransactionId') && (
                    <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                      {row.erpRef ?? <span className="opacity-40">—</span>}
                    </TableCell>
                  )}

                  {/* Error (truncated + tooltip) */}
                  {col('errorMessage') && (
                    <TableCell className={compact ? 'max-w-[140px]' : 'max-w-[200px]'}>
                      {row.errorMessage ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-error-700 dark:text-error-400 truncate block cursor-help">
                              {row.errorMessage.length > 60
                                ? row.errorMessage.slice(0, 60) + '…'
                                : row.errorMessage}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">{row.errorMessage}</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground opacity-40 text-xs">—</span>
                      )}
                    </TableCell>
                  )}

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
